import { createFileRoute, notFound } from "@tanstack/react-router";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { pageHeadForProvince, getActiveSiteUrl } from "@/lib/seo-helpers";
import { buildBreadcrumbSchema, buildFaqSchema, buildSpeakableSchema } from "@/lib/schema";
import type { ConsularCountryLanding } from "@/content/consular-corpus/countries";

function faqsEn(c: ConsularCountryLanding) {
  return [
    { q: `What are the steps to legalise a Thai document for use in ${c.countryEn}?`, a: c.chainEn.join(" → ") },
    {
      q: `Is ${c.countryEn} a party to the Apostille Convention?`,
      a: c.hague
        ? `Yes. ${c.countryEn} is a party to the HCCH 1961 Apostille Convention, but Thailand is not yet bound until 28 February 2027, so Thai documents still need MFA legalisation plus attestation at the ${c.countryEn} mission in Thailand.`
        : `No. ${c.countryEn} is not a party, so the MFA legalisation plus destination-mission attestation route continues to apply indefinitely.`,
    },
    { q: "What changes once the Convention applies to Thailand?", a: c.afterApostilleEn },
    { q: "Any specific requirements from the destination authority?", a: c.embassyNoteEn },
    { q: "What can these documents be used for?", a: c.useCaseEn },
    { q: "Why are documents commonly rejected?", a: c.pitfallsEn.join(" · ") },
    { q: "How long does it take and what does it cost?", a: c.turnaroundEn },
    {
      q: "Should the document be translated before or after legalisation?",
      a: "The Legalization Division's practice is to start from a fresh certified extract issued by the source authority, translate that, and then file the translation with the original. Translating an old photocopy is the most common cause of rework.",
    },
    {
      q: "Can your team file on my behalf?",
      a: "Yes. We work from a power of attorney bearing a THB 10 duty stamp together with certified copies of the grantor's and agent's ID. If you are outside Thailand, a Thai embassy or consulate must witness your signature first.",
    },
  ];
}

export const Route = createFileRoute("/en/consular-legalization/country/$country")({
  loader: async ({ params }: { params: { country: string } }) => {
    const { CONSULAR_COUNTRY_LANDINGS, getConsularCountryLanding } = await import("@/content/consular-corpus/countries");
    const c = getConsularCountryLanding(params.country);
    if (!c) throw notFound();
    return { c, siblings: CONSULAR_COUNTRY_LANDINGS.filter((x) => x.slug !== c.slug).map((x) => ({ slug: x.slug, label: x.countryEn })) };
  },
  component: ConsularCountryPageEN,
  head: ({ params, loaderData }: { params: { country: string }; loaderData?: { c: ConsularCountryLanding } }) => {
    if (!loaderData) {
      return { meta: [{ title: "Country not found" }, { name: "robots", content: "noindex" }] };
    }
    const site = getActiveSiteUrl();
    const { c } = loaderData;
    return {
      ...pageHeadForProvince({
        title: `Legalising Thai Documents for ${c.countryEn} (2026 Guide)`,
        description: `Step-by-step legalisation of Thai documents for use in ${c.countryEn}: MFA chain, translator rules, THB 200/400 fees, and what changes when the Apostille Convention applies to Thailand on 28 February 2027.`,
        path: `/en/consular-legalization/country/${params.country}`,
        type: "article",
        alternates: [{ lang: "th", path: `/consular-legalization/country/${params.country}` }],
        keywords: [
          `document legalisation ${c.countryEn}`,
          `MFA legalization ${c.countryEn}`,
          "Thai document attestation",
          "embassy legalisation Thailand",
          "Thailand apostille 2027",
        ],
      }),
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify(
            buildBreadcrumbSchema([
              { name: "Home", url: `${site}/en` },
              { name: "Consular Legalisation FAQ", url: `${site}/en/consular-legalization` },
              { name: c.countryEn, url: `${site}/en/consular-legalization/country/${params.country}` },
            ]),
          ),
        },
        { type: "application/ld+json", children: JSON.stringify(buildFaqSchema(faqsEn(c))) },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "HowTo",
            name: `Legalising Thai documents for ${c.countryEn}`,
            step: c.chainEn.map((s, i) => ({ "@type": "HowToStep", position: i + 1, name: s })),
          }),
        },
        { type: "application/ld+json", children: JSON.stringify(buildSpeakableSchema(`/en/consular-legalization/country/${params.country}`)) },
      ],
    };
  },
});

function ConsularCountryPageEN() {
  const { c, siblings } = Route.useLoaderData();
  const faqs = faqsEn(c);
  return (
    <>
      <SiteHeader />
      <main className="container mx-auto max-w-4xl px-4 py-10">
        <nav aria-label="breadcrumb" className="mb-4 text-sm text-muted-foreground">
          <a href="/en" className="hover:underline">Home</a> <span>/</span>{" "}
          <a href="/en/consular-legalization/faq" className="hover:underline">Consular Legalisation</a> <span>/</span>{" "}
          <span>{c.countryEn}</span>
        </nav>

        <h1 className="mb-3 text-3xl font-bold md:text-4xl">Legalising Thai Documents for {c.countryEn} (2026)</h1>
        <p className="mb-6 text-lg text-muted-foreground" data-speakable="true">
          Apostille Convention status of {c.countryEn}: <strong>{c.hague ? "party" : "not a party"}</strong>. The
          Convention enters into force for Thailand on 28 February 2027.
        </p>

        <div className="mb-8 flex flex-wrap gap-2 text-sm">
          <a href="/contact" className="rounded-md bg-primary px-4 py-2 font-medium text-primary-foreground">Get a free quote →</a>
          <a href="/en/consular-legalization/faq" className="rounded-md border px-4 py-2 font-medium hover:bg-muted">Legalisation FAQ</a>
          <a href={`/consular-legalization/country/${c.slug}`} className="rounded-md border px-4 py-2 text-muted-foreground hover:bg-muted">ภาษาไทย →</a>
        </div>

        <section className="mb-10 rounded-lg border bg-card p-6">
          <h2 className="mb-3 text-xl font-bold">Certification chain (before 28 February 2027)</h2>
          <ol className="list-decimal space-y-1 pl-6 text-sm text-muted-foreground">
            {c.chainEn.map((x: string, i: number) => <li key={i}>{x}</li>)}
          </ol>
          <p className="mt-4 text-sm text-muted-foreground"><strong>Timeline and fees:</strong> {c.turnaroundEn}</p>
        </section>

        <section className="mb-10">
          <h2 className="mb-3 text-xl font-bold">Destination-specific requirements</h2>
          <p className="text-sm leading-relaxed text-muted-foreground">{c.embassyNoteEn}</p>
        </section>

        <section className="mb-10">
          <h2 className="mb-3 text-xl font-bold">What these documents are used for</h2>
          <p className="text-sm leading-relaxed text-muted-foreground">{c.useCaseEn}</p>
        </section>

        <section className="mb-10">
          <h2 className="mb-3 text-xl font-bold">Common reasons for rejection</h2>
          <ul className="list-disc space-y-1 pl-6 text-sm text-muted-foreground">
            {c.pitfallsEn.map((x: string, i: number) => <li key={i}>{x}</li>)}
          </ul>
        </section>

        <section className="mb-10 rounded-lg border bg-muted/20 p-6">
          <h2 className="mb-3 text-xl font-bold">When the Apostille Convention applies to Thailand</h2>
          <p className="text-sm leading-relaxed text-muted-foreground">{c.afterApostilleEn}</p>
        </section>

        <section className="mb-10">
          <h2 className="mb-3 text-xl font-bold">FAQ — documents for {c.countryEn}</h2>
          <div className="space-y-3">
            {faqs.map((f, i) => (
              <details key={i} className="rounded-lg border bg-card p-4" {...(i < 3 ? { open: true } : {})}>
                <summary className="cursor-pointer text-sm font-semibold">{f.q}</summary>
                <p className="faq-answer mt-2 text-sm leading-relaxed text-muted-foreground">{f.a}</p>
              </details>
            ))}
          </div>
        </section>

        <section className="mb-10">
          <h2 className="mb-3 text-xl font-bold">Other destination countries</h2>
          <div className="flex flex-wrap gap-2 text-xs">
            {siblings.map((x: { slug: string; label: string }) => (
              <a key={x.slug} href={`/en/consular-legalization/country/${x.slug}`} className="rounded-full border bg-muted/40 px-3 py-1 text-muted-foreground hover:bg-muted">
                {x.label}
              </a>
            ))}
          </div>
        </section>

        <p className="mb-6 text-xs text-muted-foreground">
          Sources: {c.sources.join(" · ")} — information current as of August 2026. Thai legalisation steps and fees
          follow the Legalization Division, Department of Consular Affairs; Apostille Convention status follows
          hcch.net (in force for Thailand on 28 February 2027). Always confirm current requirements with the embassy
          and the receiving authority before filing.
        </p>
      </main>
      <SiteFooter />
    </>
  );
}
